<script setup>
import { onMounted, reactive, ref } from 'vue';
import {
  CdrBanner,
  CdrHeadingSerif,
  CdrContainer,
  CdrInput,
  CdrButton,
  CdrUtilitySans,
  IconErrorFill,
} from '@rei/cedar';
import { VueRecaptcha } from 'vue-recaptcha';
import CardBalanceModal from './components/modal/CardBalanceModal.vue';
import PaymentsApi from '../../APIs/PaymentsApi';
import { recordClick, recordView, setupAnalyticsClicks } from '../../common/analytics';
import { GiftCardValidators } from './common/validators';
import { ANALYTICS, FORM_ERROR_MESSAGES } from './common/constants';

const props = defineProps({
  captchaSiteKey: {
    type: Object,
    default: null,
  },
  isCaptchaEnabled: {
    type: Boolean,
    default: true,
  },
});

const state = reactive({
  cardNumber: '',
  cardPin: '',
  cardBalance: 0,
  isLoading: false,
  isModalOpen: false,
});

const errors = reactive({
  form: null,
  cardNumber: null,
  cardPin: null,
});

const isErrorFirstTime = reactive({
  cardNumber: true,
  cardPin: true,
});

const recaptchaElement = ref();

onMounted(() => {
  recordView(ANALYTICS.pageView);

  setupAnalyticsClicks();
});

const validateField = (fieldName) => {
  errors[fieldName] = null;

  const validation = GiftCardValidators[fieldName](state[fieldName]);

  if (!validation) {
    return;
  }

  if (isErrorFirstTime[fieldName]) {
    isErrorFirstTime[fieldName] = false;
  }

  errors[fieldName] = FORM_ERROR_MESSAGES[fieldName][validation];
};

const handleInputChange = (event) => {
  const { name, value } = event.target;

  state[name] = value;

  if (!isErrorFirstTime[name]) {
    validateField(name);
  }
};

const handleInputBlur = (event) => {
  const { name } = event.target;

  validateField(name);

  if (errors.form) {
    errors.form = null;
  }
};

const handleToggleModal = (value = null) => {
  state.isModalOpen = value || !state.isModalOpen;
};

const handleSubmitCaptcha = async (recaptcha) => {
  if (props.isCaptchaEnabled) {
    recaptchaElement.value.reset();
  }

  validateField('cardNumber');
  validateField('cardPin');

  if (errors.cardNumber) {
    if (errors.cardNumber === 'This field is required.') {
      recordClick({ errorCodes: `${ANALYTICS.prefix}:card number ${errors.cardNumber}` });
    } else {
      recordClick({ errorCodes: `${ANALYTICS.prefix}:${errors.cardNumber}` });
    }
    return;
  }

  if (errors.cardPin) {
    if (errors.cardPin === 'This field is required.') {
      recordClick({ errorCodes: `${ANALYTICS.prefix}:PIN ${errors.cardPin}` });
    } else {
      recordClick({ errorCodes: `${ANALYTICS.prefix}:${errors.cardPin}` });
    }
    return;
  }

  state.isLoading = true;

  const payload = {
    giftCardNumber: state.cardNumber,
    pin: state.cardPin,
    recaptcha,
  };

  const { data, error } = await PaymentsApi.getGiftCardBalance(payload);

  if (error) {
    errors.form = FORM_ERROR_MESSAGES.form.invalid;
    state.isLoading = false;
    recordClick({ errorCodes: `${ANALYTICS.prefix}:${errors.form}` });
    return;
  }

  state.cardBalance = data;
  state.isLoading = false;

  handleToggleModal(true);
};

const handleSubmitForm = async () => {
  if (props.isCaptchaEnabled) {
    recaptchaElement.value.execute();
  } else {
    handleSubmitCaptcha('no-recaptcha');
  }
};

const handleSaveCard = async () => {
  recordClick({ linkName: ANALYTICS.ui.modal.save });

  sessionStorage.setItem('newGiftCardNumber', state.cardNumber);
  window.location.href = `/account/payment-methods`;
};

const handleCloseModal = (origin) => {
  handleToggleModal(false);

  recordClick({ linkName: ANALYTICS.ui.modal[origin] });

  // Resetting card balance after closing modal. To avoid layout shift we should wait for 300ms (transition duration)
  setTimeout(() => {
    state.cardBalance = 0;
  }, 300);
};
</script>

<template>
  <cdr-container class="gift-card-balance">
    <cdr-heading-serif class="gift-card-balance__heading" tag="h1" scale="2">
      Check gift card balance
    </cdr-heading-serif>
    <cdr-utility-sans class="gift-card-balance__subheading" scale="0">
      Enter your REI gift card, bonus card, merchandise credit or refund voucher info below.
    </cdr-utility-sans>
    <form
      novalidate
      class="gift-card-balance__form"
      data-ui="gift-card-balance__form"
      @submit.prevent="handleSubmitForm"
    >
      <cdr-input
        v-model="state.cardNumber"
        label="Card number"
        name="cardNumber"
        type="text"
        maxlength="19"
        input-container-class="gift-card-balance__input-container"
        data-ui="gift-card-balance__input-card-number"
        :aria-describedby="errors.form ? 'cardNumberError' : 'cardNumber'"
        :error="errors.cardNumber"
        :disabled="state.isLoading"
        required
        @input="handleInputChange"
        @blur="handleInputBlur"
      />
      <cdr-input
        v-model="state.cardPin"
        label="PIN"
        name="cardPin"
        type="text"
        maxlength="4"
        input-container-class="gift-card-balance__input-container"
        data-ui="gift-card-balance__input-card-pin"
        :aria-describedby="errors.form ? 'cardPinError' : 'cardPin'"
        :error="errors.cardPin"
        :disabled="state.isLoading"
        required
        @input="handleInputChange"
        @blur="handleInputBlur"
      />
      <cdr-banner
        v-if="errors.form"
        class="gift-card-balance__alert--error"
        data-ui="gift-card-balance__alert"
        type="error"
        role="alert"
      >
        <template #icon-left>
          <icon-error-fill />
        </template>
        {{ errors.form }}
      </cdr-banner>
      <div class="gift-card-balance__form-actions">
        <cdr-button
          data-ui="gift-card-balance__button-submit"
          :data-analytics-id="ANALYTICS.ui.buttons.getBalance"
          type="submit"
          :disabled="state.isLoading"
        >
          Get balance
        </cdr-button>
      </div>
    </form>
    <div class="gift-card-balance__footer">
      <cdr-utility-sans scale="0" strong>Need help?</cdr-utility-sans>
      <cdr-button
        :data-analytics-id="ANALYTICS.ui.buttons.contactUs"
        tag="a"
        modifier="secondary"
        href="https://www.rei.com/help?article=contact-rei"
      >
        Contact us
      </cdr-button>
    </div>

    <card-balance-modal
      :opened="state.isModalOpen"
      :card-number="state.cardNumber"
      :card-balance="state.cardBalance"
      @close="handleCloseModal"
      @save="handleSaveCard"
    />

    <keep-alive v-if="props.isCaptchaEnabled">
      <vue-recaptcha
        ref="recaptchaElement"
        :sitekey="props.captchaSiteKey"
        size="invisible"
        load-recaptcha-script
        @verify="handleSubmitCaptcha"
      />
    </keep-alive>
  </cdr-container>
</template>

<style lang="scss">
@import '@rei/cedar/dist/cdr-reset.css';

$form-max-width: 435px;
$form-input-min-width: 158px;

.gift-card-balance {
  display: flex;
  flex-direction: column;
  gap: $cdr-space-one-x;
  margin-block: $cdr-space-two-x;
  padding-bottom: $cdr-space-two-x;

  h1.gift-card-balance__heading {
    @include cdr-text-heading-serif-800;

    @media (max-width: $cdr-breakpoint-sm) {
      @include cdr-text-heading-serif-600;
    }
  }

  p.gift-card-balance__subheading {
    @include cdr-text-utility-sans-300;
  }

  &__form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $cdr-space-one-x;
    max-width: $form-max-width;
    padding-top: $cdr-space-one-x;

    @media (max-width: $cdr-breakpoint-sm) {
      padding-top: 0;
      padding-bottom: $cdr-space-half-x;
    }

    > div:first-child {
      width: 100%;
    }

    > div:nth-child(2) > div:nth-child(2) {
      width: $form-input-min-width;
    }
  }

  &__alert--error {
    &.cdr-banner_15-0-1 .cdr-banner__message_15-0-1 {
      font-weight: 500;
      padding: $cdr-space-half-x;
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $cdr-space-one-x;
    padding-top: $cdr-space-one-and-a-half-x;

    p {
      @include cdr-text-utility-sans-strong-300;
    }
  }
}
</style>
